import React, { useState, useEffect } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import {
  FaFlag,
  FaCheck,
  FaTimes,
  FaEye,
  FaTrash,
  FaSpinner,
  FaShieldAlt,
  FaClock,
  FaCheckCircle,
  FaExclamationTriangle,
  FaFilter,
  FaSearch,
  FaArrowLeft,
} from "react-icons/fa";
import { HiOutlineSparkles } from "react-icons/hi";
import { Link } from "react-router-dom";
import toast from "react-hot-toast";

const AdminReports = () => {
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [isDarkMode, setIsDarkMode] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");

  // Check dark mode
  useEffect(() => {
    const checkDarkMode = () => {
      const dark =
        document.documentElement.getAttribute("data-theme") === "dark";
      setIsDarkMode(dark);
    };
    checkDarkMode();
    const observer = new MutationObserver(checkDarkMode);
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ["data-theme"],
    });
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    fetchReports();
  }, []);

  const fetchReports = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get("/api/reports", {
        headers: { Authorization: `Bearer ${token}` },
      });
      setReports(response.data);
    } catch (error) {
      console.error("Fetch reports error:", error);
      toast.error("Failed to fetch reports");
    } finally {
      setLoading(false);
    }
  };

  const handleResolve = async (reportId, action) => {
    try {
      const token = localStorage.getItem("token");
      await axios.put(
        `/api/reports/${reportId}`,
        { status: "resolved", action },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      toast.success(
        action === "delete_note"
          ? "Note deleted and report resolved"
          : "Report resolved",
      );
      fetchReports();
    } catch (error) {
      toast.error("Failed to resolve report");
    }
  };

  const getReasonBadge = (reason) => {
    const reasons = {
      copyright: { label: "Copyright", color: "#8b5cf6" },
      inappropriate: { label: "Inappropriate", color: "#ef4444" },
      spam: { label: "Spam", color: "#06b6d4" },
      low_quality: { label: "Low Quality", color: "#f59e0b" },
      duplicate: { label: "Duplicate", color: "#10b981" },
      other: { label: "Other", color: "#6c757d" },
    };
    const r = reasons[reason] || reasons.other;
    return (
      <span
        style={{
          background: r.color,
          color: "white",
          padding: "0.2rem 0.5rem",
          borderRadius: "20px",
          fontSize: "0.7rem",
          fontWeight: "500",
        }}
      >
        {r.label}
      </span>
    );
  };

  // Helper functions for styling
  const getTextColor = () => (isDarkMode ? "#f1f5f9" : "#1e293b");
  const getTextSecondary = () => (isDarkMode ? "#94a3b8" : "#64748b");
  const getBorderColor = () =>
    isDarkMode ? "rgba(139,92,246,0.2)" : "rgba(139,92,246,0.1)";
  const getCardBackground = () =>
    isDarkMode ? "rgba(30, 30, 45, 0.8)" : "rgba(255, 255, 255, 0.9)";
  const getInputBackground = () => (isDarkMode ? "rgba(0,0,0,0.3)" : "#ffffff");

  const filteredReports = reports.filter((report) => {
    if (filter !== "all" && report.status !== filter) return false;
    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      return (
        report.noteTitle?.toLowerCase().includes(searchLower) ||
        report.reportedBy?.name?.toLowerCase().includes(searchLower) ||
        report.reportedBy?.email?.toLowerCase().includes(searchLower) ||
        report.description?.toLowerCase().includes(searchLower)
      );
    }
    return true;
  });

  const pendingCount = reports.filter((r) => r.status === "pending").length;

  if (loading) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "60vh",
          background: isDarkMode ? "#0a0a0f" : "#f5f7fb",
        }}
      >
        <motion.div
          animate={{ rotate: 360 }}
          transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
        >
          <FaSpinner size={48} color="#8b5cf6" />
        </motion.div>
      </div>
    );
  }

  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.2,
      },
    },
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: { type: "spring", stiffness: 300, damping: 24 },
    },
  };

  return (
    <div
      style={{
        maxWidth: "1200px",
        margin: "0 auto",
        padding: "2rem",
        background: isDarkMode ? "#0a0a0f" : "#f5f7fb",
        minHeight: "100vh",
        position: "relative",
      }}
    >
      {/* Animated Background Grid */}
      <div
        style={{
          position: "fixed",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          pointerEvents: "none",
          zIndex: 0,
          backgroundImage: `radial-gradient(circle at 1px 1px, ${isDarkMode ? "rgba(139,92,246,0.1)" : "rgba(139,92,246,0.08)"} 1px, transparent 1px)`,
          backgroundSize: "40px 40px",
        }}
      />

      {/* Floating Gradient Orbs */}
      <motion.div
        animate={{ x: [0, 100, 0], y: [0, 50, 0] }}
        transition={{ duration: 20, repeat: Infinity, ease: "linear" }}
        style={{
          position: "fixed",
          top: "10%",
          left: "-5%",
          width: "250px",
          height: "250px",
          background:
            "radial-gradient(circle, rgba(139,92,246,0.3) 0%, transparent 70%)",
          borderRadius: "50%",
          filter: "blur(60px)",
          pointerEvents: "none",
          zIndex: 0,
        }}
      />
      <motion.div
        animate={{ x: [0, -100, 0], y: [0, 80, 0] }}
        transition={{ duration: 25, repeat: Infinity, ease: "linear" }}
        style={{
          position: "fixed",
          bottom: "10%",
          right: "-5%",
          width: "300px",
          height: "300px",
          background:
            "radial-gradient(circle, rgba(59,130,246,0.3) 0%, transparent 70%)",
          borderRadius: "50%",
          filter: "blur(60px)",
          pointerEvents: "none",
          zIndex: 0,
        }}
      />

      <div style={{ position: "relative", zIndex: 1 }}>
        {/* Back Button */}
        <Link
          to="/admin"
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: "0.5rem",
            color: "#8b5cf6",
            textDecoration: "none",
            fontSize: "0.9rem",
            fontWeight: 500,
            padding: "0.5rem 1rem",
            borderRadius: "10px",
            background: isDarkMode
              ? "rgba(139,92,246,0.1)"
              : "rgba(139,92,246,0.05)",
            marginBottom: "1.5rem",
            transition: "all 0.3s",
          }}
          onMouseEnter={(e) => {
            e.target.style.background = isDarkMode
              ? "rgba(139,92,246,0.2)"
              : "rgba(139,92,246,0.1)";
            e.target.style.transform = "translateX(-5px)";
          }}
          onMouseLeave={(e) => {
            e.target.style.background = isDarkMode
              ? "rgba(139,92,246,0.1)"
              : "rgba(139,92,246,0.05)";
            e.target.style.transform = "translateX(0)";
          }}
        >
          <FaArrowLeft /> Back to Admin Dashboard
        </Link>

        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, type: "spring", stiffness: 300 }}
          style={{
            background: "linear-gradient(135deg, #1e1b4b 0%, #2e1065 100%)",
            padding: "2rem",
            borderRadius: "28px",
            marginBottom: "2rem",
            position: "relative",
            overflow: "hidden",
          }}
        >
          <motion.div
            animate={{ scale: [1, 1.2, 1], opacity: [0.3, 0.1, 0.3] }}
            transition={{ duration: 4, repeat: Infinity }}
            style={{
              position: "absolute",
              top: -50,
              right: -50,
              width: 200,
              height: 200,
              background:
                "radial-gradient(circle, rgba(139,92,246,0.4) 0%, transparent 70%)",
              borderRadius: "50%",
            }}
          />
          <div
            style={{
              position: "relative",
              zIndex: 1,
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              flexWrap: "wrap",
              gap: "1rem",
            }}
          >
            <div>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "0.75rem",
                  marginBottom: "0.5rem",
                }}
              >
                <div
                  style={{
                    background: "linear-gradient(135deg, #8b5cf6, #3b82f6)",
                    width: "50px",
                    height: "50px",
                    borderRadius: "16px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <FaFlag size={24} color="white" />
                </div>
                <h1
                  style={{
                    fontSize: "1.8rem",
                    fontWeight: "bold",
                    color: "white",
                    margin: 0,
                  }}
                >
                  Reported Content
                </h1>
              </div>
              <p
                style={{
                  opacity: 0.9,
                  color: "rgba(255,255,255,0.9)",
                  marginLeft: "3rem",
                }}
              >
                Review and manage user reports on notes and content
              </p>
            </div>
            {pendingCount > 0 && (
              <div
                style={{
                  background: "rgba(239,68,68,0.2)",
                  padding: "0.5rem 1rem",
                  borderRadius: "40px",
                  display: "flex",
                  alignItems: "center",
                  gap: "0.5rem",
                }}
              >
                <FaExclamationTriangle size={16} color="#ef4444" />
                <span style={{ color: "white", fontWeight: "bold" }}>
                  {pendingCount} Pending Reports
                </span>
              </div>
            )}
          </div>
        </motion.div>

        {/* Filter Bar */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          style={{
            background: getCardBackground(),
            backdropFilter: "blur(20px)",
            borderRadius: "20px",
            padding: "1rem 1.5rem",
            marginBottom: "1.5rem",
            border: `1px solid ${getBorderColor()}`,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            flexWrap: "wrap",
            gap: "1rem",
          }}
        >
          <div style={{ display: "flex", gap: "0.5rem", flexWrap: "wrap" }}>
            {[
              { id: "all", label: "All Reports", icon: <FaFilter size={12} /> },
              {
                id: "pending",
                label: "Pending",
                icon: <FaClock size={12} />,
                color: "#f59e0b",
              },
              {
                id: "resolved",
                label: "Resolved",
                icon: <FaCheckCircle size={12} />,
                color: "#10b981",
              },
            ].map((f) => (
              <button
                key={f.id}
                onClick={() => setFilter(f.id)}
                style={{
                  padding: "0.5rem 1rem",
                  background:
                    filter === f.id
                      ? "linear-gradient(135deg, #8b5cf6, #3b82f6)"
                      : "transparent",
                  color: filter === f.id ? "white" : getTextSecondary(),
                  border:
                    filter === f.id ? "none" : `1px solid ${getBorderColor()}`,
                  borderRadius: "12px",
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  gap: "0.5rem",
                  fontSize: "0.85rem",
                  fontWeight: filter === f.id ? "600" : "500",
                  transition: "all 0.3s",
                }}
              >
                {f.icon} {f.label}
              </button>
            ))}
          </div>
          <div style={{ position: "relative" }}>
            <FaSearch
              style={{
                position: "absolute",
                left: "12px",
                top: "50%",
                transform: "translateY(-50%)",
                color: getTextSecondary(),
                fontSize: "14px",
              }}
            />
            <input
              type="text"
              placeholder="Search reports..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={{
                padding: "0.6rem 1rem 0.6rem 2.5rem",
                border: `2px solid ${getBorderColor()}`,
                borderRadius: "12px",
                fontSize: "0.85rem",
                width: "220px",
                outline: "none",
                background: getInputBackground(),
                color: getTextColor(),
                transition: "all 0.3s",
              }}
              onFocus={(e) => (e.target.style.borderColor = "#8b5cf6")}
              onBlur={(e) => (e.target.style.borderColor = getBorderColor())}
            />
          </div>
        </motion.div>

        {/* Reports List */}
        {filteredReports.length === 0 ? (
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            style={{
              textAlign: "center",
              padding: "3rem",
              background: getCardBackground(),
              backdropFilter: "blur(20px)",
              borderRadius: "24px",
              border: `1px solid ${getBorderColor()}`,
            }}
          >
            <div
              style={{
                background: isDarkMode
                  ? "rgba(139,92,246,0.1)"
                  : "rgba(139,92,246,0.05)",
                width: "80px",
                height: "80px",
                borderRadius: "40px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                margin: "0 auto 1rem auto",
              }}
            >
              <FaShieldAlt size={40} color="#8b5cf6" />
            </div>
            <h3 style={{ color: getTextColor(), marginBottom: "0.5rem" }}>
              {searchTerm ? "No matching reports found" : "All Clear!"}
            </h3>
            <p style={{ color: getTextSecondary() }}>
              {searchTerm
                ? "Try adjusting your search criteria"
                : "No reports to review at this time"}
            </p>
          </motion.div>
        ) : (
          <motion.div
            variants={containerVariants}
            initial="hidden"
            animate="visible"
            style={{ display: "grid", gap: "1rem" }}
          >
            {filteredReports.map((report) => (
              <motion.div
                key={report._id}
                variants={itemVariants}
                whileHover={{ y: -2 }}
                style={{
                  background: getCardBackground(),
                  backdropFilter: "blur(20px)",
                  padding: "1.5rem",
                  borderRadius: "20px",
                  border: `1px solid ${getBorderColor()}`,
                  borderLeft: `4px solid ${report.status === "pending" ? "#f59e0b" : "#10b981"}`,
                  transition: "all 0.3s",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "start",
                    flexWrap: "wrap",
                    gap: "1rem",
                  }}
                >
                  <div style={{ flex: 1 }}>
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "0.75rem",
                        flexWrap: "wrap",
                        marginBottom: "0.75rem",
                      }}
                    >
                      <h3
                        style={{
                          margin: 0,
                          color: getTextColor(),
                          fontSize: "1.1rem",
                          fontWeight: "bold",
                        }}
                      >
                        {report.noteTitle}
                      </h3>
                      {getReasonBadge(report.reason)}
                      {report.status === "resolved" && (
                        <span
                          style={{
                            background: "#10b981",
                            color: "white",
                            padding: "0.2rem 0.6rem",
                            borderRadius: "20px",
                            fontSize: "0.7rem",
                            display: "inline-flex",
                            alignItems: "center",
                            gap: "0.3rem",
                          }}
                        >
                          <FaCheck size={10} /> Resolved
                        </span>
                      )}
                    </div>
                    <div
                      style={{
                        display: "flex",
                        gap: "1rem",
                        flexWrap: "wrap",
                        marginBottom: "0.75rem",
                        fontSize: "0.85rem",
                      }}
                    >
                      <span style={{ color: getTextSecondary() }}>
                        Reported by:{" "}
                        <strong style={{ color: "#8b5cf6" }}>
                          {report.reportedBy?.name}
                        </strong>{" "}
                        ({report.reportedBy?.email})
                      </span>
                      <span style={{ color: getTextSecondary() }}>
                        {new Date(report.createdAt).toLocaleString()}
                      </span>
                    </div>
                    {report.description && (
                      <p
                        style={{
                          color: getTextSecondary(),
                          marginTop: "0.75rem",
                          background: isDarkMode
                            ? "rgba(139,92,246,0.05)"
                            : "rgba(139,92,246,0.03)",
                          padding: "0.75rem",
                          borderRadius: "12px",
                          fontSize: "0.85rem",
                          borderLeft: `3px solid #8b5cf6`,
                        }}
                      >
                        <strong>Details:</strong> {report.description}
                      </p>
                    )}
                    {report.adminNotes && (
                      <p
                        style={{
                          color: "#f59e0b",
                          marginTop: "0.75rem",
                          background: isDarkMode
                            ? "rgba(245,158,11,0.1)"
                            : "#fff5e6",
                          padding: "0.75rem",
                          borderRadius: "12px",
                          fontSize: "0.85rem",
                          borderLeft: `3px solid #f59e0b`,
                        }}
                      >
                        <strong>Admin Note:</strong> {report.adminNotes}
                      </p>
                    )}
                  </div>
                  {report.status === "pending" && (
                    <div
                      style={{
                        display: "flex",
                        gap: "0.75rem",
                        flexWrap: "wrap",
                      }}
                    >
                      <motion.button
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                        onClick={() =>
                          window.open(`/notes/${report.note._id}`, "_blank")
                        }
                        style={{
                          padding: "0.5rem 1rem",
                          background:
                            "linear-gradient(135deg, #06b6d4, #0891b2)",
                          color: "white",
                          border: "none",
                          borderRadius: "10px",
                          cursor: "pointer",
                          display: "flex",
                          alignItems: "center",
                          gap: "0.5rem",
                          fontSize: "0.85rem",
                          fontWeight: "500",
                        }}
                      >
                        <FaEye size={14} /> View Note
                      </motion.button>
                      <motion.button
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                        onClick={() => handleResolve(report._id, "dismiss")}
                        style={{
                          padding: "0.5rem 1rem",
                          background:
                            "linear-gradient(135deg, #10b981, #059669)",
                          color: "white",
                          border: "none",
                          borderRadius: "10px",
                          cursor: "pointer",
                          display: "flex",
                          alignItems: "center",
                          gap: "0.5rem",
                          fontSize: "0.85rem",
                          fontWeight: "500",
                        }}
                      >
                        <FaCheck size={14} /> Dismiss Report
                      </motion.button>
                      <motion.button
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                        onClick={() => handleResolve(report._id, "delete_note")}
                        style={{
                          padding: "0.5rem 1rem",
                          background:
                            "linear-gradient(135deg, #ef4444, #dc2626)",
                          color: "white",
                          border: "none",
                          borderRadius: "10px",
                          cursor: "pointer",
                          display: "flex",
                          alignItems: "center",
                          gap: "0.5rem",
                          fontSize: "0.85rem",
                          fontWeight: "500",
                        }}
                      >
                        <FaTrash size={14} /> Delete Note
                      </motion.button>
                    </div>
                  )}
                </div>
              </motion.div>
            ))}
          </motion.div>
        )}
      </div>

      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
};

export default AdminReports;

